import React, { useState, useRef, useEffect, useCallback } from 'react';

/**
 * PlayerShotProfileCard — Interactive player shooting profile
 * Displays SCS (Self-Created Shots) vs CCS (Catch-Created Shots) breakdown
 * with expandable detail view (sidebar drawer or detached modal)
 *
 * Data contract:
 * {
 *   player: { name, avatar_url, team },
 *   scs: { total_attempts, categories: [{ rank, label, fga, fgm, fg_pct, footwork, catch_to_release_avg_sec, preshot_prep_flags, video_clip_ids }] },
 *   ccs: { total_attempts, categories: [...] }
 * }
 */

// ─── Styles ──────────────────────────────────────────────────────────────────

const COLORS = {
  make: '#4CAF50',
  miss: '#EF5350',
  track: '#1a1a2e',
  bg: '#000',
  card: '#0a0a0f',
  border: 'rgba(255,255,255,0.08)',
  textPrimary: '#fff',
  textSecondary: '#888',
  textMuted: '#555',
  accent: '#60a5fa',
  flagClean: '#22c55e',
  flagMinor: '#f59e0b',
  flagBreakdown: '#ef4444',
};

// ─── Stacked Bar ─────────────────────────────────────────────────────────────

function StackedBar({ fgm, fga, label }) {
  const pct = fga > 0 ? (fgm / fga) * 100 : 0;
  const missPct = fga > 0 ? ((fga - fgm) / fga) * 100 : 0;

  return (
    <div
      style={{ width: '100%', height: 8, background: COLORS.track, borderRadius: 4, overflow: 'hidden', display: 'flex' }}
      aria-label={`${label}: ${fgm}/${fga} (${pct.toFixed(0)}%)`}
      role="meter"
      aria-valuenow={pct}
      aria-valuemin={0}
      aria-valuemax={100}
    >
      <div style={{ width: `${pct}%`, background: COLORS.make, transition: 'width 250ms ease-out' }} />
      <div style={{ width: `${missPct}%`, background: COLORS.miss, transition: 'width 250ms ease-out' }} />
    </div>
  );
}

// ─── Prep Quality Badge ──────────────────────────────────────────────────────

function PrepBadge({ flag }) {
  const flagMap = {
    'clean': { color: COLORS.flagClean, label: 'Clean' },
    'late gather': { color: COLORS.flagMinor, label: 'Late Gather' },
    'late hands': { color: COLORS.flagMinor, label: 'Late Hands' },
    'hips not square': { color: COLORS.flagBreakdown, label: 'Hips Off' },
    'hip swing needed': { color: COLORS.flagBreakdown, label: 'Hip Swing' },
    'compensatory torsion': { color: COLORS.flagBreakdown, label: 'Torsion' },
  };
  const info = flagMap[flag.toLowerCase()] || { color: COLORS.flagMinor, label: flag };

  return (
    <span style={{
      display: 'inline-block', fontSize: 9, fontWeight: 600,
      padding: '2px 6px', borderRadius: 4,
      background: `${info.color}22`, color: info.color,
      border: `1px solid ${info.color}44`,
    }}>
      {info.label}
    </span>
  );
}

// ─── Shot Category Row (Compact) ─────────────────────────────────────────────

function ShotCategoryRow({ category, rank, onClick }) {
  const pct = category.fg_pct != null ? (category.fg_pct * 100).toFixed(1) : 'N/A';

  return (
    <button
      onClick={onClick}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%',
        background: 'transparent', border: 'none', padding: '5px 0',
        cursor: 'pointer', textAlign: 'left', color: COLORS.textPrimary,
        transition: 'background 200ms', borderRadius: 4,
      }}
      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
      tabIndex={0}
      aria-label={`${category.label}: ${category.fgm}/${category.fga}, ${pct}%`}
    >
      <span style={{ fontSize: 10, color: COLORS.textMuted, width: 28, flexShrink: 0, fontFamily: 'monospace' }}>
        SC#{rank}
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 11, color: COLORS.textSecondary, marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {category.label}
        </div>
        <StackedBar fgm={category.fgm} fga={category.fga} label={category.label} />
      </div>
      <div style={{ fontSize: 11, color: COLORS.textSecondary, textAlign: 'right', flexShrink: 0, minWidth: 48 }}>
        <span style={{ color: pct !== 'N/A' && parseFloat(pct) >= 40 ? COLORS.make : COLORS.textSecondary, fontWeight: 600 }}>
          {pct}%
        </span>
        <div style={{ fontSize: 9, color: COLORS.textMuted }}>
          {category.fgm}/{category.fga}
        </div>
      </div>
    </button>
  );
}

// ─── Shot Column (SCS or CCS) ────────────────────────────────────────────────

function ShotColumn({ type, data, onCategoryClick, onColumnClick }) {
  const categories = (data?.categories || []).slice(0, 5);
  const total = data?.total_attempts || 0;
  const hoverTimerRef = useRef(null);
  const [hovered, setHovered] = useState(false);

  const handleMouseEnter = () => {
    hoverTimerRef.current = setTimeout(() => setHovered(true), 300);
  };
  const handleMouseLeave = () => {
    clearTimeout(hoverTimerRef.current);
    setHovered(false);
  };

  return (
    <div
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={onColumnClick}
      style={{
        flex: 1, minWidth: 0, cursor: 'pointer',
        padding: '8px 10px', borderRadius: 8,
        border: `1px solid ${hovered ? 'rgba(255,255,255,0.15)' : 'transparent'}`,
        transition: 'border 200ms, box-shadow 200ms',
        boxShadow: hovered ? '0 0 12px rgba(96,165,250,0.08)' : 'none',
      }}
    >
      <div style={{
        fontSize: 12, fontWeight: 700, color: COLORS.textSecondary,
        marginBottom: 8, textTransform: 'uppercase', letterSpacing: 1,
      }}>
        {type} <span style={{ color: COLORS.textMuted, fontWeight: 400 }}>({total})</span>
      </div>
      {categories.length === 0 ? (
        <div style={{ fontSize: 11, color: COLORS.textMuted, padding: '12px 0' }}>No data</div>
      ) : (
        categories.map((cat, i) => (
          <ShotCategoryRow
            key={cat.rank || i}
            category={cat}
            rank={cat.rank || i + 1}
            onClick={(e) => { e.stopPropagation(); onCategoryClick(cat); }}
          />
        ))
      )}
    </div>
  );
}

// ─── Footwork Breakdown ──────────────────────────────────────────────────────

function FootworkBreakdown({ footwork }) {
  if (!footwork) return null;
  const { one_two_cadence, hop } = footwork;

  return (
    <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
      {one_two_cadence && (
        <div style={{
          flex: 1, padding: '8px 10px', borderRadius: 6,
          border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: 'uppercase', marginBottom: 4 }}>1-2 Cadence</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>
            {one_two_cadence.fg_pct != null ? `${(one_two_cadence.fg_pct * 100).toFixed(1)}%` : 'N/A'}
          </div>
          <div style={{ fontSize: 10, color: COLORS.textMuted }}>{one_two_cadence.fgm}/{one_two_cadence.fga}</div>
        </div>
      )}
      {hop && (
        <div style={{
          flex: 1, padding: '8px 10px', borderRadius: 6,
          border: `1px solid ${COLORS.border}`,
        }}>
          <div style={{ fontSize: 10, color: COLORS.textMuted, textTransform: 'uppercase', marginBottom: 4 }}>Hop</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.textPrimary }}>
            {hop.fg_pct != null ? `${(hop.fg_pct * 100).toFixed(1)}%` : 'N/A'}
          </div>
          <div style={{ fontSize: 10, color: COLORS.textMuted }}>{hop.fgm}/{hop.fga}</div>
        </div>
      )}
    </div>
  );
}

// ─── Detail Panel ────────────────────────────────────────────────────────────

function DetailPanel({ type, data, selectedCategory, onClose }) {
  const categories = data?.categories || [];

  return (
    <div style={{ padding: 20 }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>
          {type} Detail — {data?.total_attempts || 0} Attempts
        </h3>
        <button onClick={onClose} style={{
          background: 'transparent', border: `1px solid ${COLORS.border}`,
          color: COLORS.textSecondary, borderRadius: 6, padding: '4px 10px',
          cursor: 'pointer', fontSize: 12,
        }}>Close</button>
      </div>

      {/* Category Detail Table */}
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
        <thead>
          <tr style={{ borderBottom: `1px solid ${COLORS.border}` }}>
            <th style={{ textAlign: 'left', padding: '6px 8px', color: COLORS.textMuted }}>#</th>
            <th style={{ textAlign: 'left', padding: '6px 8px', color: COLORS.textMuted }}>Shot Type</th>
            <th style={{ textAlign: 'center', padding: '6px 8px', color: COLORS.textMuted }}>FGA</th>
            <th style={{ textAlign: 'center', padding: '6px 8px', color: COLORS.textMuted }}>FGM</th>
            <th style={{ textAlign: 'center', padding: '6px 8px', color: COLORS.textMuted }}>FG%</th>
            <th style={{ textAlign: 'center', padding: '6px 8px', color: COLORS.textMuted }}>C2R (s)</th>
            <th style={{ textAlign: 'left', padding: '6px 8px', color: COLORS.textMuted }}>Prep</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((cat, i) => {
            const isSelected = selectedCategory && (cat.rank === selectedCategory.rank || cat.label === selectedCategory.label);
            return (
              <React.Fragment key={i}>
                <tr style={{
                  borderBottom: `1px solid rgba(255,255,255,0.03)`,
                  background: isSelected ? 'rgba(96,165,250,0.08)' : 'transparent',
                }}>
                  <td style={{ padding: '8px', color: COLORS.textMuted }}>{cat.rank || i + 1}</td>
                  <td style={{ padding: '8px', color: COLORS.textPrimary, fontWeight: 500 }}>{cat.label}</td>
                  <td style={{ padding: '8px', textAlign: 'center', color: COLORS.textSecondary }}>{cat.fga}</td>
                  <td style={{ padding: '8px', textAlign: 'center', color: COLORS.textSecondary }}>{cat.fgm}</td>
                  <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: cat.fg_pct >= 0.4 ? COLORS.make : COLORS.textSecondary }}>
                    {cat.fg_pct != null ? `${(cat.fg_pct * 100).toFixed(1)}%` : 'N/A'}
                  </td>
                  <td style={{ padding: '8px', textAlign: 'center', color: COLORS.textSecondary, fontFamily: 'monospace' }}>
                    {cat.catch_to_release_avg_sec != null ? `${cat.catch_to_release_avg_sec.toFixed(2)}s` : '-'}
                  </td>
                  <td style={{ padding: '8px' }}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {(cat.preshot_prep_flags || []).length === 0 ? (
                        <PrepBadge flag="clean" />
                      ) : (
                        cat.preshot_prep_flags.map((f, j) => <PrepBadge key={j} flag={f} />)
                      )}
                    </div>
                  </td>
                </tr>
                {isSelected && (
                  <tr>
                    <td colSpan={7} style={{ padding: '4px 8px 12px' }}>
                      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                        <div style={{ flex: 1, minWidth: 140 }}>
                          <StackedBar fgm={cat.fgm} fga={cat.fga} label={cat.label} />
                        </div>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <FootworkBreakdown footwork={cat.footwork} />
                        </div>
                      </div>
                      {cat.video_clip_ids?.length > 0 && (
                        <div style={{ marginTop: 8, display: 'flex', gap: 6 }}>
                          {cat.video_clip_ids.map((clipId, j) => (
                            <button key={j} style={{
                              background: 'transparent', border: `1px solid ${COLORS.border}`,
                              color: COLORS.accent, borderRadius: 4, padding: '3px 8px',
                              cursor: 'pointer', fontSize: 10, display: 'flex', alignItems: 'center', gap: 4,
                            }}>
                              <span>&#9654;</span> {clipId}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ─── Expansion Panel (Modal or Sidebar) ──────────────────────────────────────

function ExpansionPanel({ type, data, selectedCategory, onClose, mode = 'modal' }) {
  if (mode === 'sidebar') {
    const side = type === 'SCS' ? 'left' : 'right';
    return (
      <div style={{
        position: 'fixed', top: 0, [side]: 0, bottom: 0,
        width: '40vw', minWidth: 360, maxWidth: 600,
        background: COLORS.card, borderLeft: side === 'right' ? `1px solid ${COLORS.border}` : 'none',
        borderRight: side === 'left' ? `1px solid ${COLORS.border}` : 'none',
        zIndex: 9999, overflowY: 'auto',
        animation: `slideIn${side === 'left' ? 'Left' : 'Right'} 250ms ease-out`,
      }}>
        <DetailPanel type={type} data={data} selectedCategory={selectedCategory} onClose={onClose} />
      </div>
    );
  }

  // Modal mode
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      animation: 'fadeIn 200ms ease',
    }} onClick={onClose}>
      <div style={{
        background: COLORS.card, border: `1px solid ${COLORS.border}`,
        borderRadius: 12, maxWidth: 700, width: '90vw', maxHeight: '85vh',
        overflow: 'auto', animation: 'scaleIn 200ms ease',
      }} onClick={e => e.stopPropagation()}>
        <DetailPanel type={type} data={data} selectedCategory={selectedCategory} onClose={onClose} />
      </div>
    </div>
  );
}

// ─── Main Card Component ─────────────────────────────────────────────────────

export default function PlayerShotProfileCard({ playerData, expansionMode: initialMode }) {
  const [expanded, setExpanded] = useState(null); // { type: 'SCS'|'CCS', category: null|{} }
  const [expansionMode, setExpansionMode] = useState(
    initialMode || localStorage.getItem('shotcard_expansion_mode') || 'modal'
  );
  const [showSettings, setShowSettings] = useState(false);

  const handleModeChange = useCallback((mode) => {
    setExpansionMode(mode);
    localStorage.setItem('shotcard_expansion_mode', mode);
    setShowSettings(false);
  }, []);

  const handleColumnClick = useCallback((type) => {
    setExpanded({ type, category: null });
  }, []);

  const handleCategoryClick = useCallback((type, category) => {
    setExpanded({ type, category });
  }, []);

  if (!playerData) return null;

  const { player, scs, ccs } = playerData;

  return (
    <>
      {/* CSS animations */}
      <style>{`
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes scaleIn { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        @keyframes slideInLeft { from { transform: translateX(-100%); } to { transform: translateX(0); } }
        @keyframes slideInRight { from { transform: translateX(100%); } to { transform: translateX(0); } }
      `}</style>

      <div style={{
        background: COLORS.card, border: `1px solid ${COLORS.border}`,
        borderRadius: 12, overflow: 'hidden', position: 'relative',
      }}>
        {/* Settings toggle */}
        <button
          onClick={() => setShowSettings(!showSettings)}
          style={{
            position: 'absolute', top: 8, right: 8, zIndex: 2,
            background: 'transparent', border: 'none', color: COLORS.textMuted,
            cursor: 'pointer', fontSize: 14, padding: 4,
          }}
          title="Expansion mode"
        >
          &#9881;
        </button>
        {showSettings && (
          <div style={{
            position: 'absolute', top: 28, right: 8, zIndex: 3,
            background: '#111', border: `1px solid ${COLORS.border}`,
            borderRadius: 8, padding: 8, minWidth: 140,
          }}>
            <div style={{ fontSize: 10, color: COLORS.textMuted, marginBottom: 6, textTransform: 'uppercase' }}>Expansion Mode</div>
            {['modal', 'sidebar'].map(m => (
              <button key={m} onClick={() => handleModeChange(m)} style={{
                display: 'block', width: '100%', textAlign: 'left',
                background: expansionMode === m ? 'rgba(96,165,250,0.15)' : 'transparent',
                border: 'none', color: COLORS.textPrimary, padding: '4px 8px',
                borderRadius: 4, cursor: 'pointer', fontSize: 12, marginBottom: 2,
              }}>
                {m === 'modal' ? 'Detached Modal' : 'Sidebar Drawer'}
              </button>
            ))}
          </div>
        )}

        {/* Card Header */}
        <div style={{
          padding: '12px 16px', borderBottom: `1px solid ${COLORS.border}`,
          display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: COLORS.textPrimary }}>
            {player?.name || 'Unknown Player'}
          </div>
          {player?.team && (
            <span style={{ fontSize: 11, color: COLORS.textMuted }}>{player.team}</span>
          )}
        </div>

        {/* Card Body — Three Column Layout */}
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 0,
          padding: '8px 4px',
        }}>
          {/* SCS Column */}
          <ShotColumn
            type="SCS"
            data={scs}
            onColumnClick={() => handleColumnClick('SCS')}
            onCategoryClick={(cat) => handleCategoryClick('SCS', cat)}
          />

          {/* Center — Avatar */}
          <div style={{
            flexShrink: 0, display: 'flex', flexDirection: 'column',
            alignItems: 'center', padding: '8px 10px', minWidth: 80,
          }}>
            <div style={{
              width: 64, height: 64, borderRadius: 8,
              border: `2px solid ${COLORS.border}`,
              background: COLORS.track, overflow: 'hidden',
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}>
              {player?.avatar_url ? (
                <img
                  src={player.avatar_url}
                  alt={player?.name}
                  style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  onError={(e) => { e.target.style.display = 'none'; }}
                />
              ) : (
                <div style={{
                  width: '100%', height: '100%', display: 'flex',
                  alignItems: 'center', justifyContent: 'center',
                  fontSize: 24, color: COLORS.textMuted,
                }}>
                  {(player?.name || '?')[0]}
                </div>
              )}
            </div>
          </div>

          {/* CCS Column */}
          <ShotColumn
            type="CCS"
            data={ccs}
            onColumnClick={() => handleColumnClick('CCS')}
            onCategoryClick={(cat) => handleCategoryClick('CCS', cat)}
          />
        </div>
      </div>

      {/* Expansion Panel */}
      {expanded && (
        <ExpansionPanel
          type={expanded.type}
          data={expanded.type === 'SCS' ? scs : ccs}
          selectedCategory={expanded.category}
          onClose={() => setExpanded(null)}
          mode={expansionMode}
        />
      )}
    </>
  );
}
